'use client';

import { formatTwitterText } from '@benzinga/utils';

export type ShareActionName = 'Copy' | 'Twitter' | 'Facebook' | 'Email';

interface ReturnType {
  handler: (actionName: ShareActionName) => boolean | undefined;
}

export interface ShareData {
  text?: string;
  title?: string;
  url?: string;
  tickers?: string[];
}

export const useSocialShare = (data: ShareData): ReturnType => {
  const shareData: ShareData = {
    text: data.text ?? '',
    tickers: data.tickers,
    title: data.title ?? '',
    url: data.url ?? '',
  };

  const handler = (name: ShareActionName): boolean | undefined => {
    const handleClipboardCopy = () => {
      if (shareData.url) {
        copyTextToClipboard(shareData.url);
      }
    };

    const handleShareTwitter = () => {
      const text = formatTwitterText({ text: shareData.text ?? '', tickers: shareData.tickers });
      window.open(
        `https://twitter.com/intent/tweet?text=${text}`,
        'twitterwindow',
        'left=20,top=20,width=600,height=300,toolbar=0,resizable=1',
      );
    };

    const handleShareFacebook = () => {
      let url = `https://www.facebook.com/sharer.php?s=100&amp;p[summary]=${shareData.text}`;
      if (shareData.title && shareData.text) {
        url = `https://www.facebook.com/sharer.php?s=100&amp;p[title]=${shareData.title}&amp;p[summary]=${shareData.text}`;
      }
      window.open(url, 'facebookwindow', 'left=20,top=20,width=600,height=700,toolbar=0,resizable=1');
    };

    const handleShareEmail = () => {
      window.open(
        `mailto:example@website.com?subject=${shareData.title}&body=${shareData.text}`,
        '_blank',
        'noopener,noreferrer',
      );
    };

    if (name === 'Copy') {
      handleClipboardCopy();
      return true;
    } else if (name === 'Email') {
      handleShareEmail();
    } else if (name === 'Twitter') {
      handleShareTwitter();
    } else if (name === 'Facebook') {
      handleShareFacebook();
    }
    return false;
  };

  return { handler };
};

export default useSocialShare;

const fallbackCopyTextToClipboard = (text: string) => {
  const textAreaElement = document.createElement('textarea');
  textAreaElement.value = text;

  textAreaElement.setAttribute('readonly', '');
  textAreaElement.style.left = '0';
  textAreaElement.style.top = '0';
  textAreaElement.style.position = 'fixed';
  textAreaElement.style.opacity = '0';
  textAreaElement.style.width = '1px';
  textAreaElement.style.height = '0';

  document.body.appendChild(textAreaElement);
  textAreaElement.focus();
  textAreaElement.select();

  try {
    const result = document.execCommand('copy');
    document.body.removeChild(textAreaElement);
    return result;
  } catch (err) {
    console.error('Fallback: Oops, unable to copy', err);
    document.body.removeChild(textAreaElement);
  }

  return false;
};

export const copyTextToClipboard = (text: string, callback?: () => void) => {
  if (!navigator.clipboard) {
    const result = fallbackCopyTextToClipboard(text);
    if (result && callback) return callback();
  } else {
    return navigator.clipboard.writeText(text).then(callback, error => {
      console.error('Could not copy text: ', error);
    });
  }
};
